import { z } from 'zod';

import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';

export enum Metric {
  CART_COUNT = 'cart_count',
  NET_AMOUNT = 'net_amount',
  TOTAL_PAID = 'total_paid',
  NET_REVENUE = 'net_revenue',
  ALL = 'all',
}

const requestSchema = z
  .object({
    start_datetime: z.string(),
    end_datetime: z.string(),
    metric: z.nativeEnum(Metric),
    property_code: z.string().optional(),
    confirmation_code: z.string().optional(),
  })
  .refine((data) => new Date(data.end_datetime) >= new Date(data.start_datetime), {
    message: 'end_datetime must be after or equal to start_datetime',
    path: ['end_datetime'],
  });

const sharedResponseSchema = z.object({
  brandIds: z.number(),
  propertyCode: z.string().nullable(),
});

const responseSchema = z.union([
  sharedResponseSchema.merge(
    z.object({ metric: z.literal(Metric.CART_COUNT), results: z.object({ confirmationCodesCount: z.number() }) }),
  ),
  sharedResponseSchema.merge(
    z.object({ metric: z.literal(Metric.NET_AMOUNT), results: z.object({ netAmount: z.number() }) }),
  ),
  sharedResponseSchema.merge(
    z.object({ metric: z.literal(Metric.TOTAL_PAID), results: z.object({ totalPaid: z.number() }) }),
  ),
  sharedResponseSchema.merge(
    z.object({ metric: z.literal(Metric.NET_REVENUE), results: z.object({ netRevenue: z.number() }) }),
  ),
  sharedResponseSchema.merge(
    z.object({
      metric: z.literal(Metric.ALL),
      results: z.object({
        confirmationCodesCount: z.number(),

        total: z.number(),
        discountAmount: z.number(),
        subtotal: z.number(),
        taxesNotIncludedInPrice: z.number(),
        taxesIncludedInPrice: z.number(),
        totalPaid: z.number(),

        hostPayout: z.number(),
        promoterPayout: z.number(),
        creditCardFee: z.number(),
        netDeposited: z.number(),

        grossAmount: z.number(),
        grossRevenue: z.number(),

        netAmount: z.number(),
        netRevenue: z.number(),
      }),
    }),
  ),
]);

export const GetMetricsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query }: { query: z.input<typeof requestSchema> }) => `v1/metrics?${buildQueryString(query)}`,
  responseSchema,
  requestSchema,
});
